import { useEffect, useState ,useRef} from "react";
import styled from "styled-components";
import axios from "axios";
import Loader from "../components/Loader";
import AddInventoryItemModal from "../components/AddInventoryItemModal"; // assumed same folder as AddStoreModal
import FilterBar from "../components/filterBar";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Page = styled.div`
  min-height: 100vh;
  background: #f6f8fb;
  font-family: 'Segoe UI', 'Inter', Arial, sans-serif;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 18px 32px;
`;

const PageTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: #21325b;
  margin-bottom: 18px;
  flex: 6;
`;

const TableCard = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(35,62,88,0.06);
  padding: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
  }
  th {
    font-weight: 600;
    color: #2b3b5f;
  }
`;

const Button = styled.button`
  background: #3772FF;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 2rem;
  min-width: 80px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(35,62,88,0.05);
  &:hover {
    background: #245bb9;
  }
  margin: 10px 0;
`;

const Row = styled.div`
  display: flex;
  gap: 22px;
  margin-bottom: 0.3rem;
`;

export default function InventoryPage() {
  const userData = {
    user: JSON.parse(localStorage.getItem('user')),
    token: localStorage.getItem('access_token'),
  };
const fileInputRef = useRef(null);
const [excelFile, setExcelFile] = useState(null);

  const isAdmin = userData.user.roles.role_name === "SUPER_ADMIN";
  const [isLoading,setIsLoading] = useState(false)
  const [allProducts,setAllProducts] = useState([])
  const [products, setProducts] = useState([]);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const headers = { Authorization: `Bearer ${userData.token}` };
const [filterText, setFilterText] = useState("");
const [filterFromDate, setFilterFromDate] = useState(null);
const [filterToDate, setFilterToDate] = useState(null);
const [sortField, setSortField] = useState(null);
const [sortOrder, setSortOrder] = useState(null);

  const navigate = useNavigate()
const hostname = window.location.hostname;
  const isInventoryDomain = hostname === 'inventory.fescafe.in' || hostname === 'localhost';
  const isRecordsDomain = hostname === 'records.fescafe.in' ;
useEffect(() => {
    if(!isAdmin)
      navigate(isRecordsDomain ?'/cashflow' : '/inventory/entries')
  }, []);
const downloadSampleExcel = () => {
  // Sample data extracted from your attached file
  const sampleData = [
    { name: "Milk Powder", metric: "kg", category_name: "Dairy Products" },
    { name: "Black Coffee", metric: "bags", category_name: "Beverages" },
    { name: "Corn Flakes", metric: "pack", category_name: "Breakfast Items" },
    { name: "Butter Croissant", metric: "pcs", category_name: "Bakery" },
    { name: "Frozen Peas", metric: "kg", category_name: "Frozen Foods" },
  ];

  // Create worksheet from JSON data
  const worksheet = XLSX.utils.json_to_sheet(sampleData);

  // Create a new workbook and append the worksheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "ProductCategoriesSample");

  // Write workbook and trigger download
  XLSX.writeFile(workbook, "Product_Upload_Excel_Sample.xlsx");
};

const handleFileChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const allowedTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];
  
  if (!allowedTypes.includes(file.type)) {
    alert('Invalid file type. Please select an Excel (.xls or .xlsx) file.');
    return;
  }
  
  if (file.size > 5 * 1024 * 1024) {
    alert('File size exceeds 5MB limit.');
    return;
  }

  setIsLoading(true);

  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`${BACKEND_URL}/inventory/import-excel`, formData, {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.status === 200 || response.status === 201) {
      alert(`Products imported successfully! Inserted: ${response.data.inserted || 0}`);
      fetchProducts();
    } else {
      alert('Upload completed with unexpected response.');
    }

  } catch (error) {
    console.error('Error uploading Excel:', error);
    if (error.response?.data?.error) {
      alert(`Import failed: ${error.response.data.error}`);
    } else {
      alert('Import failed due to a server/network error.');
    }
  } finally {
    setIsLoading(false);
  }
};

useEffect(() => {
  let filtered = allProducts;

  // Text filter on name, metric, category name
  if (filterText) {
    const lowerSearch = filterText.toLowerCase();
    filtered = filtered.filter(
      (p) =>
        (p.name?.toLowerCase().includes(lowerSearch) ||
          (p.metric?.toLowerCase().includes(lowerSearch)) ||
          (p.category?.category_name?.toLowerCase().includes(lowerSearch)))
    );
  }

  // Date filter on created_at
  if (filterFromDate || filterToDate) {
    filtered = filtered.filter((p) => {
      const createdAt = new Date(p.created_at);
      const from = filterFromDate ? new Date(filterFromDate) : null;
      const to = filterToDate ? new Date(filterToDate) : null;
      return (!from || createdAt >= from) && (!to || createdAt <= to);
    });
  }

  // Sort
  if (sortField && sortOrder) {
    filtered = [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === "created_at" || sortField === "updated_at") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else {
        aVal = aVal || "";
        bVal = bVal || "";
      }
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }

  setProducts(filtered);
}, [allProducts, filterText, filterFromDate, filterToDate, sortField, sortOrder]);

const handleTextFilter = (text) => setFilterText(text || "");
const handleDateFilter = (from, to) => {
  setFilterFromDate(from || null);
  setFilterToDate(to || null);
};
const handleSortChange = (field, order) => {
  setSortField(field);
  setSortOrder(order);
};
const handleResetFilters = () => {
  setFilterText("");
  setFilterFromDate(null);
  setFilterToDate(null);
  setSortField(null);
  setSortOrder(null);
};

const exportToExcel = () => {
  const data = products.map((p) => ({
    ID: p.id,
    Name: p.name,
    Metric: p.metric,
    Category: p.category?.category_name || "",
    CreatedBy: `${p.created_by.first_name} ${p.created_by.last_name}`,
    CreatedOn: new Date(p.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
    ModifiedBy: `${p.updated_by.first_name} ${p.updated_by.last_name}`,
    ModifiedOn: new Date(p.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory Products");
  XLSX.writeFile(workbook, "products_export.xlsx");
};

const exportToPDF = () => {
  const doc = new jsPDF();
  const columns = [
    { header: "ID", dataKey: "id" },
    { header: "Name", dataKey: "name" },
    { header: "Metric", dataKey: "metric" },
    { header: "Category", dataKey: "category" },
    { header: "Created By", dataKey: "createdBy" },
    { header: "Created On", dataKey: "createdOn" },
    { header: "Modified By", dataKey: "modifiedBy" },
    { header: "Modified On", dataKey: "modifiedOn" },
  ];

  const rows = products.map((p) => ({
    id: p.id,
    name: p.name,
    metric: p.metric,
    category: p.category?.category_name || "",
    createdBy: `${p.created_by.first_name} ${p.created_by.last_name}`,
    createdOn: new Date(p.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
    modifiedBy: `${p.updated_by.first_name} ${p.updated_by.last_name}`,
    modifiedOn: new Date(p.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
  }));

  autoTable(doc, { columns, body: rows, margin: { top: 10 }, styles: { fontSize: 8 } });
  doc.save("products_export.pdf");
};

  const fetchProducts = async () => {
    setIsLoading(true)
    try {
      const res = await axios.get(`${BACKEND_URL}/inventory`, { headers });
      setProducts(res.data.sort((a, b) => a.id - b.id));
      setAllProducts(res.data.sort((a, b) => a.id - b.id))
          setIsLoading(false)

    } catch (err) {
                setIsLoading(false)
      console.error("Error fetching inventory products:", err.message);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const openAddModal = () => {
    setIsEditMode(false);
    setEditProduct(null);
    setIsAddProductModalOpen(true);
  };

  const openEditModal = (product) => {
    setIsEditMode(true);
    setEditProduct(product);
    setIsAddProductModalOpen(true);
  };
const handleDeleteProduct = async (productId) => {
  if (!window.confirm("Are you sure you want to delete this product?")) return;

  setIsLoading(true);
  try {
    await axios.get(`${BACKEND_URL}/inventory/delete/${productId}`, { headers });
    // Refetch products after deletion
    await fetchProducts();
  } catch (err) {
    console.error("Error deleting product:", err.message);
  } finally {
    setIsLoading(false);
  }
};

  return (
    <>
      {isLoading && <Loader/>}
      <Page>
        <Container>
          <Row>
            <PageTitle>Inventory Products</PageTitle>
            {isAdmin && <Button onClick={openAddModal}>Add Product</Button>}
            <input
  type="file"
  accept=".xls,.xlsx"
  ref={fileInputRef}
  style={{ display: 'none' }}
  onChange={handleFileChange}
/>
<Button
  onClick={() => fileInputRef.current && fileInputRef.current.click()}
  disabled={isLoading}
>
  Upload Excel
</Button>
<Button
  onClick={() => downloadSampleExcel()}
  disabled={isLoading}
>
  Import Format(Excel)
</Button>
          </Row>
          <Row>
            <FilterBar
  placeholder="Search Products..."
  sortFields={[
    { value: "name", label: "Name" },
    { value: "metric", label: "Metric" },
    { value: "category_name", label: "Category" }, // This requires handling, see note
    { value: "created_at", label: "Created Date" },
  ]}
  onTextFilter={handleTextFilter}
  onDateFilter={handleDateFilter}
  onSortChange={handleSortChange}
  onResetFilters={handleResetFilters}
  onExportPDF={exportToPDF}
  onExportExcel={exportToExcel}
/>

          </Row>
          <TableCard>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Metric</th>
                  <th>Category</th>
                  <th>Created by</th>
                  <th>Created on</th>
                  <th>Last modified by</th>
                  <th>Last modified on</th>
                  <th>Action</th>
                  
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{p.name}</td>
                    <td>{p.metric}</td>
                    <td>{p?.category?.category_name}</td>
                    <td>{`${p.created_by.first_name} ${p.created_by.last_name}`}</td>
                    <td>{new Date(p.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</td> 
                    <td>{`${p.updated_by.first_name} ${p.updated_by.last_name}`}</td>
                    <td>{new Date(p.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</td> 
                    
                    <td>
                      <Button
                        onClick={() => openEditModal(p)}
                        disabled={!isAdmin}
                      >
                        Edit
                      </Button>
                    </td>
                    <td>
  <Button
    onClick={() => handleDeleteProduct(p.id)}
    disabled={!isAdmin}
    style={{ marginLeft: '8px', backgroundColor: '#d9534f' }}
  >
    Delete
  </Button>
</td>

                  </tr>
                ))}
              </tbody>
            </Table>
          </TableCard>

          {isAddProductModalOpen && (
            <AddInventoryItemModal
              isOpen={isAddProductModalOpen}
              isEdit={isEditMode}
              editProduct={editProduct}
              onClose={() => {
                setIsAddProductModalOpen(false);
                setEditProduct(null);
                setIsEditMode(false);
              }}
              onProductAdded={fetchProducts}
              onProductUpdated={fetchProducts}
            />
          )}
        </Container>
      </Page>
    </>
  );
}
